import { Injectable } from '@angular/core';
import { RouterStateSnapshot, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { map, filter, take, catchError } from 'rxjs/operators';
import { combineLatest, of, Observable } from 'rxjs';

import { SettingsService } from 'src/app/admin/settings/settings.service';
import { AuthService } from './auth.service';

/**
 * Admin role name
 *
 * @type {*}
 */
const ADMIN_ROLE = 'Administrator';

/**
 * Guard to prevent access when Maintenance Mode is enabled
 *
 * @export
 * @class NotInMaintenanceModeGuard
 * @implements {CanActivate}
 */
@Injectable({
  providedIn: 'root'
})
export class NotInMaintenanceModeGuard {
  /**
   * Creates an instance of NotInMaintenanceModeGuard.
   *
   * @param {Router} router Angular router service
   * @param {SettingsService} settingsService Settings service
   * @memberof NotInMaintenanceModeGuard
   */
  constructor(
    private readonly router: Router,
    private settingsService: SettingsService,
    private authService: AuthService
  ) { }

  /**
   * Check whether mode is enabled and whether the user can bypass
   *
   * @param {ActivatedRouteSnapshot} route Angular route service
   * @param {RouterStateSnapshot} state Angular route state service
   * @return {*}
   * @memberof NotInMaintenanceModeGuard
   */
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Wait until userRole is a concrete value (not null)
    const roleReady$ = this.authService.userRole$.pipe(
      filter(role => role !== null), // must be a value
      take(1)
    );

    const maintenance$ = this.settingsService.checkMaintenanceMode().pipe(take(1));

    return combineLatest([roleReady$, maintenance$]).pipe(
      map(([role, maintenanceMode]) => {
        if (role === ADMIN_ROLE) {
          return true;
        } else {
          if (maintenanceMode.isEnabled) {
            return this.router.parseUrl('/maintenance-mode');
          } else {
            return true;
          }
        }
      }),
      catchError(() => of(true))
    );
  }
}
